// The daily boss encounter: a pure function of the UTC day index. belch runs this exact file
// to verify a submitted run, so it must stay dependency-free (no Angular, no env, no address
// config) and its draw order must never change — reordering a draw rewrites every past day.

import { DAILY_BOSS_TABLE, type DailyBossMon, type DailyBossMove } from './daily-boss-table';

export type DailyField = 'flux' | 'elemental' | 'unstable';

/** Draw 1 picks from this array by index; the order is part of the frozen schedule. */
export const DAILY_FIELDS: readonly DailyField[] = ['flux', 'elemental', 'unstable'];

export const DAILY_TEAM_SIZE = 4;
/** Engine cap on equipped moves; also the popcount of every moveSelections byte we emit. */
export const DAILY_MOVES_PER_MON = 4;
/** Field-scored shortlist the team is drawn from — wide enough to vary day to day. */
const CANDIDATE_POOL = 8;

export interface DailyEncounter {
  readonly day: number;
  readonly field: DailyField;
  /** 0 = singles, 1 = doubles. */
  readonly battleMode: 0 | 1;
  readonly monIndices: readonly number[];
  readonly facetIds: readonly number[];
  /** Per-slot 8-bit loadout masks; bit i selects catalog lane i. */
  readonly moveSelections: readonly number[];
  readonly leadMonId: number;
}

const MS_PER_DAY = 86_400_000;

export function utcDayIndex(atMs: number = Date.now()): number {
  return Math.floor(atMs / MS_PER_DAY);
}

export function dayStartMs(day: number): number {
  return day * MS_PER_DAY;
}

/** mulberry32, seeded off a scrambled day so consecutive days don't correlate. */
function makeDayRng(day: number): () => number {
  let s = (Math.imul(day, 0x9e3779b1) ^ 0x5bf03635) >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const isAttack = (m: DailyBossMove) => m.cls === 'phys' || m.cls === 'spec';

/** Uniform pick. Non-null because every caller passes a non-empty array — belch compiles this
 *  same file under noUncheckedIndexedAccess, so the assertion has to be explicit. */
function pick<T>(from: readonly T[], rng: () => number): T {
  return from[Math.floor(rng() * from.length)]!;
}

// ---------------------------------------------------------------------------
// Field fitness. Each field punishes a different thing, so the boss team is
// picked on the axis that field actually rewards.
// ---------------------------------------------------------------------------

function monScore(mon: DailyBossMon, field: DailyField): number {
  const attacks = mon.moves.filter(isAttack);
  const bestPower = attacks.reduce((n, m) => Math.max(n, m.power), 0);
  switch (field) {
    // Every active is dragged out each turn, so a mon only gets value from what it
    // does the turn it arrives: immediate damage and speed, no setup runway.
    case 'unstable':
      return bestPower * 2 + mon.speed - mon.moves.filter((m) => m.cls === 'self').length * 40;
    // A random status lands every turn, so the team has to absorb chip indefinitely.
    case 'elemental':
      return mon.hp + mon.defense + mon.specialDefense;
    // Round-end stamina swings ±2, so cheap moves keep acting through a bad roll.
    case 'flux':
      return attacks.reduce((n, m) => Math.max(n, (m.power / (m.stamina + 1)) * 4), 0) + mon.speed / 2;
  }
}

function moveScore(move: DailyBossMove, field: DailyField): number {
  const base = isAttack(move) ? move.power : 0;
  switch (field) {
    case 'unstable':
      // A stat boost is spent the moment the field drags the mon back out.
      return move.cls === 'self' ? base - 200 : base - move.stamina * 5;
    case 'elemental':
      return move.cls === 'self' || move.cls === 'other' ? base - 30 : base;
    case 'flux':
      return (base / (move.stamina + 1)) * 4 - (move.cls === 'self' ? 60 : 0);
  }
}

const FACET_POOLS: Record<DailyField, readonly number[]> = {
  // Atk+ facets — the field rewards hitting hard on arrival.
  unstable: [4, 5, 6],
  // HP+ / Def+ facets — the field rewards outlasting the status chip.
  elemental: [1, 2, 3, 7, 8, 9],
  // Speed+ facets — moving first matters more when stamina is unreliable.
  flux: [10, 11, 12],
};

/** Top `n` by score, ties broken by mon id so the ordering is stable across engines. */
function shortlist(field: DailyField, n: number): DailyBossMon[] {
  return DAILY_BOSS_TABLE
    .map((mon) => ({ mon, score: monScore(mon, field) }))
    .sort((a, b) => (b.score - a.score) || (a.mon.id - b.mon.id))
    .slice(0, n)
    .map((e) => e.mon);
}

/** The 4 highest-scoring lanes, ties by lane, packed as a selection bitmap. */
function loadoutMask(mon: DailyBossMon, field: DailyField): number {
  const lanes = mon.moves
    .map((move) => ({ lane: move.lane, score: moveScore(move, field) }))
    .sort((a, b) => (b.score - a.score) || (a.lane - b.lane))
    .slice(0, DAILY_MOVES_PER_MON);
  return lanes.reduce((mask, l) => mask | (1 << l.lane), 0);
}

/**
 * Draw order is the contract: field, mode, team, facets, loadouts — in that order, off one
 * stream. Any insertion or reorder changes every past day's encounter.
 */
export function deriveDailyEncounter(day: number): DailyEncounter {
  const rng = makeDayRng(day);

  const field = pick(DAILY_FIELDS, rng);
  const battleMode: 0 | 1 = rng() < 0.5 ? 0 : 1;

  const pool = shortlist(field, CANDIDATE_POOL);
  const team: DailyBossMon[] = [];
  for (let i = 0; i < DAILY_TEAM_SIZE; i++) {
    team.push(pool.splice(Math.floor(rng() * pool.length), 1)[0]!);
  }

  const facetPool = FACET_POOLS[field];
  const facetIds = team.map(() => pick(facetPool, rng));

  const moveSelections = team.map((mon) => loadoutMask(mon, field));

  return {
    day,
    field,
    battleMode,
    monIndices: team.map((m) => m.id),
    facetIds,
    moveSelections,
    leadMonId: team[0]!.id,
  };
}

/** Canonical form both repos hash, so a derivation drift shows up as a mismatch. */
export function encounterHash(e: DailyEncounter): string {
  const canonical = [
    e.day, e.field, e.battleMode,
    e.monIndices.join(','), e.facetIds.join(','), e.moveSelections.join(','),
  ].join('|');
  let h = 0x811c9dc5;
  for (let i = 0; i < canonical.length; i++) {
    h ^= canonical.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

export function bossMonById(id: number): DailyBossMon | undefined {
  return DAILY_BOSS_TABLE.find((m) => m.id === id);
}

/** The four moves a slot actually brings, in lane order — for the boss preview UI. */
export function loadoutMoves(monId: number, mask: number): readonly DailyBossMove[] {
  const mon = bossMonById(monId);
  if (!mon) return [];
  return mon.moves.filter((m) => (mask >> m.lane) & 1);
}
